import React, { useCallback, useState } from "react";
import { ChevronDown, MessageSquare } from "lucide-react";
import { api } from "../api/client";

/**
 * Wave 5 (2026-10-06) -- Text history (P-8) on the CRM patient card.
 *
 * Same look as CollapsibleSection, but it loads on FIRST OPEN only: the
 * timeline reads n8n's event log in BigQuery, which should not run every
 * time anyone opens any patient card. Read-only except two actions, both
 * two clicks with no browser pop-up (the Mark handled pattern):
 *   - Resend: a fresh copy of an appointment confirmation (sales/admin; the
 *     server enforces STOP, upcoming-only and once per day).
 *   - Retry: the existing Needs Attention retry for a failed/expired text.
 * Replies show by category only; the words are not stored (P-6 adds them).
 */

const KIND_LABEL = { yes: "YES", no: "NO", stop: "STOP", start: "START", help: "HELP", other: "Other" };
// #28: replies loaded from before the turn-on are stored as handled by "backfill".
// #29 (Matt's #28 note): nobody acted on those in the portal, so they read
// "earlier reply" - never "handled".
const BACKFILL_BY = "backfill";
// P-7 (Oct 12): a plain YES the portal applied (stage + T-05) is handled by "auto-confirm".
const AUTO_CONFIRM_BY = "auto-confirm";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const fmtAppt = (a) => (a && a.day && a.date && a.time ? `${a.day}, ${a.date} at ${a.time}` : null);

function statusPill(t) {
  if (t.delivery === "delivered") return ["Delivered", "bg-emerald-50 text-emerald-700 border-emerald-200"];
  if (t.delivery === "failed") return ["Not delivered", "bg-rose-50 text-rose-700 border-rose-200"];
  if (t.status === "sent") return ["Sent", "bg-slate-50 text-slate-700 border-slate-200"];
  if (t.status === "expired") return ["Expired", "bg-amber-50 text-amber-800 border-amber-200"];
  if (t.status === "failed") return ["Held", "bg-amber-50 text-amber-800 border-amber-200"];
  return ["Queued", "bg-blue-50 text-blue-700 border-blue-200"];
}

const HELD_REASON = (r) => {
  const s = String(r || "").toLowerCase();
  if (s.includes("duplicate")) return "already texted this number recently";
  if (s.includes("opt_out") || s.includes("opt-out")) return "number is on the opt-out list";
  return s ? s.replace(/_/g, " ") : "";
};

const BLOCK_REASON = (r) => {
  const s = String(r || "");
  if (s.includes("no_consent")) return "no texting consent";
  if (s.includes("no_phone")) return "no phone on file";
  if (s.includes("bad_phone")) return "phone number not textable";
  return s.replace(/_/g, " ");
};

export function TextHistoryPanel({ patient, userRole }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(null); // "resend-12" | "retry-12"
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const canAct = userRole === "sales" || userRole === "admin";

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get(`/crm/patients/${patient.id}/texts`);
      setData(res.data);
    } catch (e) {
      setError("Text history couldn't load. Try again in a moment.");
    }
  }, [patient.id]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) load();
  };

  const act = async (kind, rowId) => {
    const key = `${kind}-${rowId}`;
    if (confirming !== key) {
      setConfirming(key);
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      if (kind === "resend") await api.post(`/crm/patients/${patient.id}/texts/${rowId}/resend`);
      else await api.post(`/crm/sms/needs-attention/${rowId}/retry`);
      setNote(kind === "resend" ? "Confirmation queued. It sends within the 8 AM to 8 PM Pacific window." : "Retry queued.");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setNote(d === "already_resent_today" ? "Already resent today." : "That didn't go through. Refresh and try again.");
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  const sentCount = data ? data.texts.length : 0;
  const replyCount = data ? data.replies.length : 0;
  const timeline = data
    ? [
        ...data.texts.map((t) => ({ kind: "text", at: t.sent_at || t.queued_at, t })),
        ...data.replies.map((r) => ({ kind: "reply", at: r.at, r })),
        ...data.blocks.map((b) => ({ kind: "block", at: b.first_seen_at, b })),
      ].sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")))
    : [];

  return (
    <div className="mt-2 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="text-history-panel">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
        data-testid="text-history-toggle"
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <MessageSquare size={14} className="text-slate-400" />
          Text history
          {data && (
            <span className="normal-case font-normal text-slate-500" data-testid="text-history-summary">
              · {sentCount} {sentCount === 1 ? "text" : "texts"} · {replyCount} {replyCount === 1 ? "reply" : "replies"}
            </span>
          )}
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 text-xs" data-testid="text-history-body">
          {error && <p className="text-rose-700">{error}</p>}
          {!error && !data && <p className="text-slate-500">Loading…</p>}
          {data && timeline.length === 0 && <p className="text-slate-500">No texts yet.</p>}
          {data && timeline.map((item, i) => {
            if (item.kind === "reply") {
              return (
                <div key={`r${i}`} className="ml-4 pl-2 border-l-2 border-blue-200 py-1.5" data-testid="text-history-reply">
                  <span className="font-medium text-slate-800">Patient replied: {KIND_LABEL[item.r.kind] || "Other"}</span>
                  <span className="text-slate-500"> · {fmtWhen(item.r.at)}</span>
                  {item.r.handled && item.r.handled_by === BACKFILL_BY ? (
                    <span className="text-slate-500" data-testid="text-history-reply-earlier"> · earlier reply</span>
                  ) : item.r.handled && item.r.handled_by === AUTO_CONFIRM_BY ? (
                    <span className="text-emerald-700" data-testid="text-history-reply-auto"> · confirmed, T-05 sent</span>
                  ) : item.r.handled ? (
                    <span className="text-slate-500" data-testid="text-history-reply-handled">
                      {" · handled"}{item.r.handled_by && item.r.handled_by !== BACKFILL_BY ? ` by ${item.r.handled_by}` : ""}
                    </span>
                  ) : item.r.kind === "other" || item.r.kind === "help" || item.r.kind === "no" ? (
                    <span className="text-amber-700"> · call the patient</span>
                  ) : item.r.kind === "yes" ? (
                    <span className="text-amber-700" data-testid="text-history-reply-yes-open"> · confirm by hand</span>
                  ) : null}
                  {item.r.words ? (
                    <div className="mt-0.5 text-slate-700 whitespace-pre-wrap break-words" data-testid="text-history-reply-words">
                      “{item.r.words}”
                    </div>
                  ) : null}
                </div>
              );
            }
            if (item.kind === "block") {
              const b = item.b;
              return (
                <div key={`b${i}`} className="py-1.5 border-t border-slate-100 flex justify-between gap-2" data-testid="text-history-block">
                  <span className="text-slate-700">
                    Not sent: {b.label || b.template} · {BLOCK_REASON(b.reason)}
                    <span className="text-slate-500"> · {fmtWhen(b.first_seen_at)}</span>
                  </span>
                  <span className={b.handled ? "text-slate-500" : "text-amber-700"}>{b.handled ? "Handled" : "Needs attention"}</span>
                </div>
              );
            }
            const t = item.t;
            const [pillText, pillClass] = statusPill(t);
            const appt = fmtAppt(t.appointment);
            return (
              <div key={`t${t.row_id}`} className="py-1.5 border-t border-slate-100 flex justify-between gap-2" data-testid="text-history-text">
                <div>
                  <div className="text-slate-800">
                    {t.label}{t.is_resend ? " (resent)" : ""}
                  </div>
                  <div className="text-slate-500">
                    {fmtWhen(t.sent_at || t.queued_at)}{appt ? ` · ${appt}` : ""}
                    {t.status === "failed" && HELD_REASON(t.failure_reason) ? ` · ${HELD_REASON(t.failure_reason)}` : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${pillClass}`}>{pillText}</span>
                  {canAct && t.can_resend && (
                    <button type="button" disabled={busy} onClick={() => act("resend", t.row_id)}
                            className="block mt-1 ml-auto text-[11px] font-semibold text-blue-700 underline"
                            data-testid={`text-history-resend-${t.row_id}`}>
                      {confirming === `resend-${t.row_id}` ? "Confirm resend" : "Resend"}
                    </button>
                  )}
                  {canAct && t.can_retry && (
                    <button type="button" disabled={busy} onClick={() => act("retry", t.row_id)}
                            className="block mt-1 ml-auto text-[11px] font-semibold text-blue-700 underline"
                            data-testid={`text-history-retry-${t.row_id}`}>
                      {confirming === `retry-${t.row_id}` ? "Confirm retry" : "Retry"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {data && (
            <p className="mt-2 pt-2 border-t border-slate-100 text-slate-500" data-testid="text-history-footer">
              {data.consent.stopped ? "Patient replied STOP: texts are off." : "Not opted out."}
              {!data.delivery_available ? " Delivery details are unavailable right now." : ""}
            </p>
          )}
          {note && <p className="mt-1 text-slate-700" role="status" data-testid="text-history-note">{note}</p>}
        </div>
      )}
    </div>
  );
}

export default TextHistoryPanel;
